import { History } from 'history';
import invariant from 'invariant';
import queryString from 'query-string';
import React, { createRef } from 'react';
import ReactDOM from 'react-dom';
import deepEqual from 'react-fast-compare';
import { connect } from 'react-redux';
import styled from 'styled-components';

import { IdealistDaysMapBlockData } from 'api/cms';
import { CurrentUser } from 'api/currentUser';
import {
  Pin,
  PinInfo,
  Pins,
  deletePin,
  fetchGroups,
  fetchIdealistDaysEvents,
  fetchPin,
  fetchPins,
  setPin,
} from 'api/idealistDaysMaps';
import { PublicPage } from 'api/website';
import Box from 'components/Box';
import GroupsMapAddPinConfirm from 'components/GroupsMapAddPinConfirm';
import IdealistDaysMap from 'components/IdealistDay/IdealistDaysMap';
import IdealistDaysMapAddPinConfirm from 'components/IdealistDay/IdealistDaysMapAddPinConfirm';
import IdealistDaysMapConvertPinToEventConfirm from 'components/IdealistDay/IdealistDaysMapConvertPinToEventConfirm';
import { FilestackImage } from 'components/Image';
import SearchHitForMap from 'components/Search/SearchHitForMap';
import ScrollWatcherContainer from 'containers/ScrollWatcherContainer';
import { setMapState } from 'store/main/ducks/daysMap';
import { setGMapsError } from 'store/main/ducks/gMapsError';
import { showModal as showModalRaw } from 'store/main/ducks/modal';
import { isBrowser } from 'utils/browser';
import googleMaps from 'utils/googleMaps';
// eslint-disable-next-line import/no-namespace
import * as localStorage from 'utils/localStorage';
import trackError from 'utils/trackError';

const PinText = styled.div`
  white-space: pre-line;
`;
const shareModalTitle = getText(
  'Thank you! Please share this map with your friends.'
);
const shareModalMessage = getText(
  'Join me in connecting our community on Idealist'
);
// Used for testing.
const testMode = localStorage.getItem('testMode');
const PinImageContainer = styled(Box)`
  img {
    width: 100%;
  }
`;
type Props = {
  blockId: string;
  data: IdealistDaysMapBlockData;
  currentUser: CurrentUser;
  currentUserIsStaff: boolean;
  showModal: typeof showModalRaw;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  updateCurrentUser: (...args: Array<any>) => any;
  content: PublicPage;
  history: History;
  gMapsErrored: boolean;
  setMapState: (arg0: ('DAYS' | 'GROUPS') | null | undefined) => void;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setExposeSiteNav: (...args: Array<any>) => any;
};
type Marker = {
  pinId: string;
  data: {
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setMap: (...args: Array<any>) => any;
  };
};
type Markers = Array<Marker>;
type MapResultPoint = {
  lat: number;
  lng: number;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  hits: Array<Record<string, any>>;
};
type State = {
  date: string;
  fullScreen: boolean;
  eventOrGroupCount: number | null | undefined;
  userPinCount: number | null | undefined;
  initialLoad: boolean;
  loaded: boolean;
  showDates: boolean;
  showInfoBox: boolean;
  pins: Pins;
  idealistDayEventOrGroupPins: Array<MapResultPoint>;
  markers: Markers;
  idealistDayEventOrGroupMarkers: Markers;
  initialized: boolean;
};
const MARKER_ICONS = {
  IDEALIST_DAY_EVENT: '/static/images/icons/map-marker-days-event.png',
  OWN_PIN: '/static/images/icons/map-marker-days-self.png',
  GENERIC_PIN: '/static/images/icons/map-marker-days-others.png',
};

const createInfoWindow = (
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  maps: Record<string, any>,
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  node: React.ReactElement<any>
) => {
  const wrapper = document.createElement('div');
  const infoWindow = new maps.InfoWindow({
    content: wrapper,
  });
  ReactDOM.render(node, wrapper);
  return infoWindow;
};

const LOCAL_STORAGE_PIN_DATA_KEY = 'addingDaysMapPin';

const rememberPinData = (pinData: PinInfo): void => {
  localStorage.setItem(LOCAL_STORAGE_PIN_DATA_KEY, pinData, true);
};

const getRememberedPinData = (): PinInfo | null | undefined =>
  localStorage.getItem(LOCAL_STORAGE_PIN_DATA_KEY);

const clearRememberedPinData = (): void => {
  localStorage.removeItem(LOCAL_STORAGE_PIN_DATA_KEY);
};

const getCurrentUserIsStaff = (currentUser: CurrentUser) =>
  currentUser.permissions && currentUser.permissions.includes('staff');

class IdealistDaysMapBlock extends React.Component<Props, State> {
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line react/state-in-constructor
  state = {
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line react/destructuring-assignment
    date: this.props.data.mapId,
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line react/destructuring-assignment
    fullScreen: this.props.data.defaultFullscreen,
    eventOrGroupCount: null,
    userPinCount: null,
    initialLoad: false,
    loaded: false,
    showDates: false,
    showInfoBox: false,
    pins: [],
    idealistDayEventOrGroupPins: [],
    idealistDayEventOrGroupMarkers: [],
    markers: [],
    initialized: false,
  };

  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line react/no-unused-class-component-methods
  fullScreenControlEl = null;

  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line react/no-unused-class-component-methods
  counterEl = null;

  mapRef = createRef<Element>();

  // TODO(arnav): Fix this hack that's getting around a Flow issue.
  map = {};

  infoBoxInitialized = false;

  // TODO(arnav): Fix this hack that's getting around a Flow issue.
  mapsApi = {};

  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line react/no-unused-class-component-methods
  potentialMarker = null;

  openInfowindow = null;

  centerInitialized = false;

  shareProps = {
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line react/destructuring-assignment
    listing: !this.props.data.socialShareLink
      ? // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line react/destructuring-assignment
        this.props.content
      : {
          // TODO: Fix this the next time the file is edited.
          // eslint-disable-next-line react/destructuring-assignment
          url: this.props.data.socialShareLink.data.url,
        },
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line react/destructuring-assignment
    listingType: this.props.data.socialShareLink ? undefined : 'CMS_PAGE',
    title: shareModalTitle,
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line react/destructuring-assignment
    messageOverride: this.props.data.socialShareMessage || shareModalMessage,
  };

  componentDidMount() {
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line react/destructuring-assignment
    this.props.setMapState(
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line react/destructuring-assignment
      this.state.fullScreen ? this.props.data.mapType : null
    );
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    const pinData = getRememberedPinData();
    const notLoggedIn =
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line react/destructuring-assignment
      !this.props.currentUser.id && this.props.currentUser.isLoaded;

    if (
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line react/destructuring-assignment
      prevProps.currentUser !== this.props.currentUser &&
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line react/destructuring-assignment
      this.props.currentUser.location &&
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line react/destructuring-assignment
      this.state.loaded &&
      !this.centerInitialized
    ) {
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'LatLng' does not exist on type '{}'.
      const center = new this.mapsApi.LatLng(
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line react/destructuring-assignment
        this.props.currentUser.location.latitude,
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line react/destructuring-assignment
        this.props.currentUser.location.longitude
      );
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'setCenter' does not exist on type '{}'.
      this.map.setCenter(center);
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'setZoom' does not exist on type '{}'.
      this.map.setZoom(8);
    }

    if (
      pinData &&
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line react/destructuring-assignment
      prevProps.currentUser !== this.props.currentUser &&
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line react/destructuring-assignment
      this.props.currentUser.firstName &&
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line react/destructuring-assignment
      this.props.currentUser.lastName &&
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line react/destructuring-assignment
      this.props.currentUser.email
    ) {
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line react/destructuring-assignment
      this.props.showModal('ADD_IDEALIST_DAYS_MAP_PIN', {
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line react/destructuring-assignment
        prompt: this.props.data.prompt,
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line react/destructuring-assignment
        date: this.state.date,
        addPin: this.addPin,
        rememberPinData,
        clearRememberedPinData,
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line react/destructuring-assignment
        blockId: this.props.blockId,
        initialValues: pinData,
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line react/destructuring-assignment
        mapType: this.props.data.mapType,
      });
      clearRememberedPinData();
    } else if (
      !this.infoBoxInitialized &&
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line react/destructuring-assignment
      prevProps.currentUser !== this.props.currentUser && // TODO: Fix this the next time the file is edited. // Don't show infobox if Information Required has to be filled out.
      // eslint-disable-next-line react/destructuring-assignment
      (!this.props.currentUser.id ||
        notLoggedIn || // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line react/destructuring-assignment
        (this.props.currentUser.firstName &&
          // TODO: Fix this the next time the file is edited.
          // eslint-disable-next-line react/destructuring-assignment
          this.props.currentUser.lastName && // TODO: Fix this the next time the file is edited.
          // eslint-disable-next-line react/destructuring-assignment
          this.props.currentUser.email))
    ) {
      this.setShowInfoBox(true);
    }

    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line react/destructuring-assignment
    if (!this.infoBoxInitialized && this.props.currentUser.isLoaded) {
      this.infoBoxInitialized = true;
    }

    // @ts-expect-error ts-migrate(2339) FIXME: Property 'version' does not exist on type '{}'.
    if (!this.mapsApi.version) {
      return;
    }

    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line react/destructuring-assignment
    if (this.state.fullScreen !== prevState.fullScreen) {
      this.updateFullScreen();
    }

    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line react/destructuring-assignment
    if (this.state.date !== prevState.date) {
      this.loadIdealistDayEvents();
      this.loadPins();
    }

    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line react/destructuring-assignment
    if (!deepEqual(this.state.pins, prevState.pins)) {
      this.updatePinsOnMap(prevState.pins);
    }

    if (
      !deepEqual(
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line react/destructuring-assignment
        this.state.idealistDayEventOrGroupPins,
        prevState.idealistDayEventOrGroupPins
      )
    ) {
      this.updateIdealistDayEventsOnMap();
    }
  }

  createMap = () =>
    googleMaps()
      .then((maps) => {
        this.mapsApi = maps;
        const mapEl = this.mapRef.current;
        if (!mapEl) return;
        const position = new maps.LatLng(0, 0);
        const map = new maps.Map(mapEl, {
          center: position,
          zoom: 3,
          mapTypeControl: false,
          streetViewControl: false,
          // TODO: Fix this the next time the file is edited.
          // eslint-disable-next-line react/destructuring-assignment
          gestureHandling: this.state.fullScreen ? 'greedy' : 'cooperative',
          fullscreenControl: false,
          // remove Google's UI for fullscreen toggle
          clickableIcons: false,
        });
        this.map = map;
        maps.event.addListenerOnce(map, 'idle', () => {
          const mapParent = mapEl.parentElement;

          if (mapParent) {
            // tests wait for this to know that the map is loaded
            mapParent.setAttribute('data-maps-loaded', 'true');
          }
        });
        map.addListener('click', (e) => this.clickMap(e));
        map.addListener('zoom_changed', () => {
          // TODO: Fix this the next time the file is edited.
          // eslint-disable-next-line react/destructuring-assignment
          if (!this.state.initialLoad && this.state.showInfoBox) {
            this.setShowInfoBox(false);
          }
        });
      })
      .catch((error) => {
        setGMapsError(error.message);
      });

  updateFullScreen = () => {
    // @ts-expect-error ts-migrate(2339) FIXME: Property 'setOptions' does not exist on type '{}'.
    this.map.setOptions({
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line react/destructuring-assignment
      gestureHandling: this.state.fullScreen ? 'greedy' : 'cooperative',
    });
  };

  toggleFullScreen = () => {
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line react/destructuring-assignment
    this.props.setExposeSiteNav(!this.state.fullScreen);
    this.setState((prevState) => {
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line react/destructuring-assignment
      this.props.setMapState(
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line react/destructuring-assignment
        prevState.fullScreen ? null : this.props.data.mapType
      );
      return {
        showInfoBox: false,
        fullScreen: !prevState.fullScreen,
      };
    });
  };

  loadIdealistDayEvents = () => {
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line react/destructuring-assignment
    fetchIdealistDaysEvents(this.state.date).then((hits) => {
      const points = this.hitsToMapResultPoints(hits);
      this.setIdealistDayEventOrGroupPins(points);
    });
  };

  loadGroups = () => {
    fetchGroups().then((hits) => {
      const points = this.hitsToMapResultPoints(hits);
      this.setIdealistDayEventOrGroupPins(points);
    });
  };

  loadPins = () =>
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line react/destructuring-assignment
    fetchPins(this.state.date).then((pins: Array<Pin>) =>
      this.setPins(pins, true)
    );

  // eslint-disable-next-line class-methods-use-this
  hitsToMapResultPoints = (
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    hits: Array<Record<string, any>>
  ): Array<MapResultPoint> => {
    const points = hits.reduce((acc, hit) => {
      const { lat, lng } = hit._geoloc; // eslint-disable-line no-underscore-dangle

      const pointKey = `${lat}|${lng}`;

      if (!acc[pointKey]) {
        // eslint-disable-next-line no-param-reassign
        acc[pointKey] = {
          lat,
          lng,
          hits: [],
        };
      }

      acc[pointKey].hits.push(hit);
      return acc;
    }, {});
    return Object.keys(points).map((key) => points[key]);
  };

  // Events
  addIdealistDaysMapPinOnMap = (point: MapResultPoint) => {
    const markerIcon = {
      url: MARKER_ICONS.IDEALIST_DAY_EVENT,
      scaledSize: new google.maps.Size(33, 45),
    };
    // @ts-expect-error ts-migrate(2339) FIXME: Property 'LatLng' does not exist on type '{}'.
    const markerPosition = new this.mapsApi.LatLng(point.lat, point.lng);
    // @ts-expect-error ts-migrate(2339) FIXME: Property 'Marker' does not exist on type '{}'.
    const marker = new this.mapsApi.Marker({
      map: this.map,
      position: markerPosition,
      icon: markerIcon,
    });

    if (testMode) {
      marker.setTitle(point.hits[0].name);
    }

    const infoWindow = createInfoWindow(
      this.mapsApi,
      <>
        {point.hits.map((hit, index) => (
          <Box
            key={hit.objectID}
            mt={index > 0 ? '4px' : '2px'}
            width={[`calc(100vw - 48px)`, 400, 480]}
            px="4px"
          >
            <SearchHitForMap
              // @ts-expect-error ts-migrate(2769) FIXME: No overload matches this call.
              // TODO: Fix this the next time the file is edited.
              // eslint-disable-next-line react/destructuring-assignment
              currentUser={this.props.currentUser}
              hit={hit}
              history={null}
            />
          </Box>
        ))}
      </>
    );
    marker.addListener('click', () => {
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line react/destructuring-assignment
      if (this.state.showInfoBox) {
        this.setShowInfoBox(false);
      }

      infoWindow.open(this.map, marker);
      this.openInfowindow = infoWindow;
    });
    // @ts-expect-error ts-migrate(2339) FIXME: Property 'event' does not exist on type '{}'.
    this.mapsApi.event.addListener(infoWindow, 'closeclick', () => {
      this.openInfowindow = null;
    });
    return {
      pinId: point.hits[0].objectId,
      data: marker,
    };
  };

  updateIdealistDayEventsOnMap = () => {
    const { idealistDayEventOrGroupMarkers, idealistDayEventOrGroupPins } =
      this.state;
    idealistDayEventOrGroupMarkers.forEach((idealistDayMarker) =>
      idealistDayMarker.data.setMap(null)
    );
    const newIdealistDayEventMarkers = idealistDayEventOrGroupPins.map(
      this.addIdealistDaysMapPinOnMap
    );
    this.setState({
      eventOrGroupCount: idealistDayEventOrGroupPins.reduce(
        (acc, cur) => acc + cur.hits.length,
        0
      ),
      idealistDayEventOrGroupMarkers: newIdealistDayEventMarkers,
    });
  };

  updatePinsOnMap = (prevPins: Pins) => {
    const { markers, pins } = this.state;
    // If a pin in the current state is not equal to any member of the previous
    // state, that means it a new pin.
    const newPins = pins.filter((pin) => {
      if (prevPins.length === 0) return true;
      return !prevPins.some((prevPin) => prevPin.id === pin.id);
    });
    // If a pin does exist but has different data, that means it's been
    // updated
    const modifiedPins = pins.filter((pin) => {
      const { id: pinId, ...pinData } = pin;
      if (prevPins.length === 0) return true;
      return prevPins.some((prevPin) => {
        const { id: prevPinId, ...prevPinData } = prevPin;
        return prevPinId === pinId && !deepEqual(prevPinData, pinData);
      });
    });
    // If a pin in the previous state is not equal to any member of the current
    // state, that means it an old pin. It is also considered an old pin if
    // the two values have the same id but are not equal, since we'll replace it
    // with the updated new pin with the same id.
    const oldPins = prevPins.filter((prevPin) => {
      const { id: prevPinId, ...prevPinData } = prevPin;
      return !pins.some((pin) => {
        const { id: pinId, ...pinData } = pin;
        return pinId === prevPinId && deepEqual(prevPinData, pinData);
      });
    });
    // If a pin is newly created it should be animated and prompt
    // the Idealist Day Events modal, otherwise it should not.
    // But they are all updated and should be added to the state.
    const updatedPins = newPins
      .map((newPin) => ({
        data: newPin,
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line react/destructuring-assignment
        animated: !this.state.initialLoad,
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line react/destructuring-assignment
        justAdded: !this.state.initialLoad,
      }))
      .concat(
        modifiedPins.map((modifiedPin) => ({
          data: modifiedPin,
          animated: false,
          justAdded: false,
        }))
      );
    const filteredMarkers = markers.filter((marker) =>
      oldPins.forEach((oldPin) => {
        if (oldPin.id === marker.pinId) {
          marker.data.setMap(null);
          return false;
        }

        return true;
      })
    );
    const newMarkers = updatedPins.map((updatedPin) =>
      this.addPinOnMap(
        updatedPin.data,
        updatedPin.animated,
        updatedPin.justAdded
      )
    );
    this.setState({
      initialLoad: false,
      userPinCount: pins.length,
      markers: filteredMarkers.concat(newMarkers),
    });
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line prefer-destructuring
    const pinId = queryString.parse(window.location.search).pinId;
    const pin = pinId ? pins.find((p) => p.id === pinId) : null;

    if (pin) {
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line react/destructuring-assignment
      this.props.showModal('VIEW_IDEALIST_DAYS_MAP_PIN', {
        pin,
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line react/destructuring-assignment
        date: this.state.date,
        isCurrentUserPin: false,
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line react/destructuring-assignment
        currentUserIsStaff: this.props.currentUserIsStaff,
        updatePin: this.updatePin,
        deletePin: this.deletePin,
        convertPin: this.createEvent,
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line react/destructuring-assignment
        mapType: this.props.data.mapType,
      });
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'LatLng' does not exist on type '{}'.
      const center = new this.mapsApi.LatLng(pin.latitude, pin.longitude);
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'setCenter' does not exist on type '{}'.
      this.map.setCenter(center);
      this.zoomIn(pin.latitude, pin.longitude, 20);
    }
  };

  addPinOnMap = (pin: Pin, animated = false, justAdded = false) => {
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line react/destructuring-assignment
    const isCurrentUserPin = this.props.currentUser.id === pin.userId;
    // @ts-expect-error ts-migrate(2339) FIXME: Property 'LatLng' does not exist on type '{}'.
    const markerPosition = new this.mapsApi.LatLng(pin.latitude, pin.longitude);
    // @ts-expect-error ts-migrate(2339) FIXME: Property 'Marker' does not exist on type '{}'.
    const marker = new this.mapsApi.Marker({
      map: this.map,
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'Animation' does not exist on type '{}'.
      animation: animated ? this.mapsApi.Animation.DROP : null,
      position: markerPosition,
      icon: {
        url: isCurrentUserPin ? MARKER_ICONS.OWN_PIN : MARKER_ICONS.GENERIC_PIN,
        scaledSize: new google.maps.Size(33, 45),
      },
    });

    if (testMode) {
      marker.setTitle(isCurrentUserPin ? 'selfmarker' : pin.text);
    }

    const infoWindow = createInfoWindow(
      this.mapsApi,
      <PinText>{pin.text}</PinText>
    );
    marker.addListener('mouseover', () => {
      if (pin.withProfile) {
        fetchPin(pin.id).then((pinData) => {
          if (pinData.profile && pinData.profile.image) {
            const wrapper = document.createElement('div');
            infoWindow.setContent(wrapper);
            const node = (
              // TODO: Fix this the next time the file is edited.
              // eslint-disable-next-line react/jsx-no-useless-fragment
              <>
                <PinText>
                  <Box display="flex" alignItems="flex-start">
                    {pinData.profile && pinData.profile.image && (
                      <PinImageContainer
                        height={100}
                        width={100}
                        mr={16}
                        flex="0 0 auto"
                      >
                        <FilestackImage
                          round
                          data={pinData.profile.image}
                          height={100}
                          width={100}
                          alt={`Image of ${pinData.profile.firstName || ''} ${
                            pinData.profile.lastName || ''
                          }`}
                        />
                      </PinImageContainer>
                    )}
                    <Box>{pin.text}</Box>
                  </Box>
                </PinText>
              </>
            );
            ReactDOM.render(node, wrapper);
          }
        });
      }

      infoWindow.open(this.map, marker);
    });
    marker.addListener('mouseout', () => {
      infoWindow.close();
    });
    marker.addListener('click', () => {
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line react/destructuring-assignment
      if (this.state.showInfoBox) {
        this.setShowInfoBox(false);
      }

      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line react/destructuring-assignment
      this.props.showModal('VIEW_IDEALIST_DAYS_MAP_PIN', {
        pin,
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line react/destructuring-assignment
        date: this.state.date,
        isCurrentUserPin,
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line react/destructuring-assignment
        currentUserIsStaff: this.props.currentUserIsStaff,
        updatePin: this.updatePin,
        deletePin: this.deletePin,
        convertPin: this.createEvent,
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line react/destructuring-assignment
        mapType: this.props.data.mapType,
      });
    });

    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line react/destructuring-assignment
    if (justAdded && this.props.data.mapType === 'DAYS') {
      const convertToEventinfoWindow = createInfoWindow(
        this.mapsApi,
        <IdealistDaysMapConvertPinToEventConfirm
          // TODO: Fix this the next time the file is edited.
          // eslint-disable-next-line react/destructuring-assignment
          mapType={this.props.data.mapType}
          confirmConvertPin={() => this.createEvent(pin)}
          cancelConvertPin={() => {
            convertToEventinfoWindow.close();
            // TODO: Fix this the next time the file is edited.
            // eslint-disable-next-line react/destructuring-assignment
            this.props.showModal('SHARE_LISTING', this.shareProps);
          }}
        />
      );
      setTimeout(() => {
        convertToEventinfoWindow.open(this.map, marker);
      }, 500);
    }

    return {
      pinId: pin.id,
      data: marker,
    };
  };

  createEvent = (pin: Pin) => {
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line react/destructuring-assignment
    this.props.history.push(`/post-idealist-day`, {
      description: pin.text.split('\n').join('<br/>'),
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line react/destructuring-assignment
      date: this.state.date,
      pinId: pin.id,
    });
  };

  createGroup = () => {
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line react/destructuring-assignment
    this.props.history.push('/groups/create');
  };

  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  goToAddress = (coordinates: Record<string, any>) => {
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line react/destructuring-assignment
    if (this.state.showInfoBox) {
      this.setShowInfoBox(false);
    }

    this.zoomIn(coordinates.latitude, coordinates.longitude, 13);
  };

  updatePinOnMap = (pin: Pin) => {
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line react/destructuring-assignment
    this.setPins(this.state.pins.map((p) => (p.id === pin.id ? pin : p)));
  };

  removePinFromMap = (pin: Pin) => {
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line react/destructuring-assignment
    const remainingPins: Array<Pin> = this.state.pins.filter(
      (p: Pin) => p.id !== pin.id
    );
    this.setPins(remainingPins);
  };

  setDate = (date: string | null | undefined) => {
    if (date) {
      this.setState({
        date,
      });
    }

    this.setState({
      showDates: false,
    });
  };

  setPins = (pins: Pins, initialLoad?: boolean) =>
    this.setState({
      pins,
      initialLoad: initialLoad || false,
    });

  setIdealistDayEventOrGroupPins = (pins: Array<MapResultPoint>) =>
    this.setState({
      idealistDayEventOrGroupPins: pins,
    });

  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line react/no-unused-class-component-methods
  setMarkers = (markers: Markers) =>
    this.setState({
      markers,
    });

  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line react/no-unused-class-component-methods
  setIdealistDayEventMarkers = (markers: Markers) =>
    this.setState({
      markers,
    });

  setShowDates = (showDates: boolean) =>
    this.setState({
      showDates,
    });

  setShowInfoBox = (showInfoBox: boolean) => {
    this.setState({
      showInfoBox,
    });
  };

  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  clickMap = (e: Record<string, any>) => {
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line react/destructuring-assignment
    if (this.state.showInfoBox) {
      this.setShowInfoBox(false);
    }

    this.centerInitialized = true;
    const userHasPinOnMap =
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line react/destructuring-assignment
      this.props.currentUser.id && // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line react/destructuring-assignment
      this.state.pins.filter((pin) => pin.userId === this.props.currentUser.id)
        .length > 0;

    if (userHasPinOnMap) {
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line react/destructuring-assignment
      this.props.showModal('IDEALIST_DAYS_MAP_ALERT', {
        message: getText(
          'You already have a pin on this map. If you’d like to change it or delete it, please click on it.'
        ),
      });
      return;
    }

    if (this.openInfowindow) {
      this.openInfowindow.close();
    }

    const lat = e.latLng.lat();
    const lng = e.latLng.lng();

    // @ts-expect-error ts-migrate(2339) FIXME: Property 'zoom' does not exist on type '{}'.
    if (this.map.zoom < 8) {
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line react/destructuring-assignment
      this.props.showModal('IDEALIST_DAYS_MAP_ZOOM', {
        zoomIn: () => this.zoomIn(lat, lng, 8),
      });
      return;
    }

    // @ts-expect-error ts-migrate(2339) FIXME: Property 'zoom' does not exist on type '{}'.
    if (this.map.zoom < 13) {
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line react/destructuring-assignment
      this.props.showModal('IDEALIST_DAYS_MAP_ZOOM', {
        zoomIn: () => this.zoomIn(lat, lng, 13),
      });
      return;
    }

    this.addPotentialMarkerOnMap(lat, lng);
  };

  addPotentialMarkerOnMap = (latitude: number, longitude: number) => {
    // @ts-expect-error ts-migrate(2339) FIXME: Property 'LatLng' does not exist on type '{}'.
    const markerPosition = new this.mapsApi.LatLng(latitude, longitude);
    const markerIcon = {
      url: MARKER_ICONS.OWN_PIN,
      scaledSize: new google.maps.Size(33, 45),
    };
    // @ts-expect-error ts-migrate(2339) FIXME: Property 'Marker' does not exist on type '{}'.
    const potentialMarker = new this.mapsApi.Marker({
      map: this.map,
      position: markerPosition,
      icon: markerIcon,
    });

    const removePotentialMarker = () => {
      if (potentialMarker) {
        potentialMarker.setMap(null);
      }
    };

    const confirmAddPin = () => {
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line react/destructuring-assignment
      this.props.showModal('ADD_IDEALIST_DAYS_MAP_PIN', {
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line react/destructuring-assignment
        prompt: this.props.data.prompt,
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line react/destructuring-assignment
        date: this.state.date,
        addPin: this.addPin,
        rememberPinData,
        clearRememberedPinData,
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line react/destructuring-assignment
        blockId: this.props.blockId,
        initialValues: {
          text: '',
          latitude,
          longitude,
        },
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line react/destructuring-assignment
        mapType: this.props.data.mapType,
      });
      removePotentialMarker();
    };

    let infoWindow;

    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line react/destructuring-assignment
    if (this.props.data.mapType === 'DAYS') {
      infoWindow = createInfoWindow(
        this.mapsApi,
        <IdealistDaysMapAddPinConfirm
          confirmAddPin={confirmAddPin}
          cancelAddPin={removePotentialMarker}
        />
      );
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line react/destructuring-assignment
    } else if (this.props.data.mapType === 'GROUPS') {
      infoWindow = createInfoWindow(
        this.mapsApi,
        <GroupsMapAddPinConfirm
          confirmAddPin={confirmAddPin}
          confirmCreateGroup={this.createGroup}
          cancelAddPin={removePotentialMarker}
        />
      );
    }

    invariant(infoWindow, 'Infowindow not handled for this map type.');
    // @ts-expect-error ts-migrate(2339) FIXME: Property 'event' does not exist on type '{}'.
    this.mapsApi.event.addListener(
      infoWindow,
      'closeclick',
      removePotentialMarker
    );
    infoWindow.open(this.map, potentialMarker);
    // @ts-expect-error ts-migrate(2339) FIXME: Property 'addListener' does not exist on type '{}'... Remove this comment to see the full error message
    const mapClickListener = this.map.addListener('click', () => {
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'event' does not exist on type '{}'.
      this.mapsApi.event.removeListener(mapClickListener);
      removePotentialMarker();
    });
  };

  zoomIn = (lat: number, lng: number, zoom: number) => {
    // @ts-expect-error ts-migrate(2339) FIXME: Property 'LatLng' does not exist on type '{}'.
    const position = new this.mapsApi.LatLng(lat, lng);
    // @ts-expect-error ts-migrate(2339) FIXME: Property 'panTo' does not exist on type '{}'.
    this.map.panTo(position);
    // @ts-expect-error ts-migrate(2339) FIXME: Property 'setZoom' does not exist on type '{}'.
    this.map.setZoom(zoom);
  };

  addPin = (pinData: PinInfo): Promise<void> =>
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line react/destructuring-assignment
    setPin(this.state.date, pinData).then((pin) => {
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line react/destructuring-assignment
      this.setPins(this.state.pins.concat([pin]));
    });

  updatePin = (pinData: PinInfo): Promise<Pin> =>
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line react/destructuring-assignment
    setPin(this.state.date, pinData).then((pin) => {
      this.updatePinOnMap(pin);
      return pin;
    });

  deletePin = (pin: Pin): Promise<void> =>
    deletePin(pin.id).then(() => this.removePinFromMap(pin));

  render() {
    const initializeMap = () => {
      this.setState({
        initialized: true,
      });
      this.createMap().then(() => {
        const pinData = getRememberedPinData();

        // Only triggered in the Google sign *up* case.
        if (
          pinData &&
          // TODO: Fix this the next time the file is edited.
          // eslint-disable-next-line react/destructuring-assignment
          this.props.currentUser.firstName &&
          // TODO: Fix this the next time the file is edited.
          // eslint-disable-next-line react/destructuring-assignment
          this.props.currentUser.lastName &&
          // TODO: Fix this the next time the file is edited.
          // eslint-disable-next-line react/destructuring-assignment
          this.props.currentUser.email
        ) {
          // TODO: Fix this the next time the file is edited.
          // eslint-disable-next-line react/destructuring-assignment
          this.props.showModal('ADD_IDEALIST_DAYS_MAP_PIN', {
            // TODO: Fix this the next time the file is edited.
            // eslint-disable-next-line react/destructuring-assignment
            prompt: this.props.data.prompt,
            // TODO: Fix this the next time the file is edited.
            // eslint-disable-next-line react/destructuring-assignment
            date: this.state.date,
            addPin: this.addPin,
            rememberPinData,
            clearRememberedPinData,
            // TODO: Fix this the next time the file is edited.
            // eslint-disable-next-line react/destructuring-assignment
            blockId: this.props.blockId,
            initialValues: pinData,
            // TODO: Fix this the next time the file is edited.
            // eslint-disable-next-line react/destructuring-assignment
            mapType: this.props.data.mapType,
          });
          clearRememberedPinData();
        }

        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line prefer-destructuring, react/destructuring-assignment
        const mapType = this.props.data.mapType;

        if (mapType === 'DAYS') {
          this.loadIdealistDayEvents();
        } else if (mapType === 'GROUPS') {
          this.loadGroups();
        } else {
          trackError(`The map type is not handled on load: ${mapType}`);
        }

        this.loadPins();
        this.setState({
          loaded: true,
        });

        if (testMode) {
          // @ts-expect-error ts-migrate(2339) FIXME: Property 'mapLoaded' does not exist on type 'Windo... Remove this comment to see the full error message
          window.mapLoaded = true;
        }
      });
    };

    return (
      <ScrollWatcherContainer offset={[200, 200, 200, 200]}>
        {({ isInViewport }) => {
          if (
            // TODO: Fix this the next time the file is edited.
            // eslint-disable-next-line react/destructuring-assignment
            (this.state.fullScreen || isInViewport) &&
            // TODO: Fix this the next time the file is edited.
            // eslint-disable-next-line react/destructuring-assignment
            !this.state.initialized &&
            // TODO: Fix this the next time the file is edited.
            // eslint-disable-next-line react/destructuring-assignment
            this.props.currentUser.isLoaded &&
            isBrowser()
          ) {
            initializeMap();
          }

          return (
            <IdealistDaysMap
              // TODO: Fix this the next time the file is edited.
              // eslint-disable-next-line react/destructuring-assignment
              mapType={this.props.data.mapType}
              // TODO: Fix this the next time the file is edited.
              // eslint-disable-next-line react/destructuring-assignment
              instructions={this.props.data.instructions}
              // TODO: Fix this the next time the file is edited.
              // eslint-disable-next-line react/destructuring-assignment
              buttonText={this.props.data.buttonText}
              // TODO: Fix this the next time the file is edited.
              // eslint-disable-next-line react/destructuring-assignment
              date={this.state.date}
              // TODO: Fix this the next time the file is edited.
              // eslint-disable-next-line react/destructuring-assignment
              eventOrGroupCount={this.state.eventOrGroupCount}
              // TODO: Fix this the next time the file is edited.
              // eslint-disable-next-line react/destructuring-assignment
              fullScreen={this.state.fullScreen}
              // TODO: Fix this the next time the file is edited.
              // eslint-disable-next-line react/destructuring-assignment
              loaded={this.state.loaded}
              selectAddress={this.goToAddress}
              setDate={this.setDate}
              setShowDates={this.setShowDates}
              setShowInfoBox={this.setShowInfoBox}
              shareProps={this.shareProps}
              // TODO: Fix this the next time the file is edited.
              // eslint-disable-next-line react/destructuring-assignment
              showDates={this.state.showDates}
              // TODO: Fix this the next time the file is edited.
              // eslint-disable-next-line react/destructuring-assignment
              showInfoBox={this.state.showInfoBox}
              toggleFullScreen={this.toggleFullScreen}
              // TODO: Fix this the next time the file is edited.
              // eslint-disable-next-line react/destructuring-assignment
              userPinCount={this.state.userPinCount}
              // @ts-expect-error ts-migrate(2322) FIXME: Type 'RefObject<Element>' is not assignable to typ... Remove this comment to see the full error message
              ref={this.mapRef}
              // TODO: Fix this the next time the file is edited.
              // eslint-disable-next-line react/destructuring-assignment
              gMapsErrored={this.props.gMapsErrored}
            />
          );
        }}
      </ScrollWatcherContainer>
    );
  }
}

const mapStateToProps = ({ currentUser }) => ({
  currentUserIsStaff: getCurrentUserIsStaff(currentUser),
});

// eslint-disable-next-line import/no-default-export
export default connect(mapStateToProps, {
  setMapState,
  showModal: showModalRaw,
})(IdealistDaysMapBlock);
